/**
 * Vigilance — Détection intelligente des factures récurrentes manquantes.
 *
 * Port 1:1 de BE-TAYE (Vigilance.jsx), adapté SaaS multi-tenant :
 * - Sender email configurable sans restriction de domaine
 * - Endpoint /api/vigilance/* multi-tenant
 * - Réutilise ClientKey stable (client_id) au lieu du nom brut
 */
import { useEffect, useMemo, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import { motion, AnimatePresence } from "framer-motion";
import {
  AlertTriangle, EyeOff, Mail, Calendar, CheckCircle, Info, Euro,
  RefreshCw, Settings, Send, History, AlertCircle, TrendingUp,
} from "lucide-react";
import { toast } from "sonner";
import MissingVsLastMonthButton from "@/components/MissingVsLastMonthButton";

const fmtEUR = (v) => new Intl.NumberFormat("fr-FR",
  { style: "currency", currency: "EUR", minimumFractionDigits: 0 }).format(v || 0);
const fmtDate = (d) => d ? new Date(d).toLocaleDateString("fr-FR") : "—";

const genMonths = () => {
  const opts = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    opts.push({
      value: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`,
      label: d.toLocaleDateString("fr-FR", { month: "long", year: "numeric" }),
    });
  }
  return opts;
};

const PriorityBadge = ({ priority }) => {
  const cfg = {
    high: "bg-red-100 text-red-700 border-red-200",
    medium: "bg-amber-100 text-amber-700 border-amber-200",
    low: "bg-slate-100 text-slate-600 border-slate-200",
  };
  const label = { high: "Haute", medium: "Moyenne", low: "Basse" };
  return <Badge className={cfg[priority] || cfg.low}>{label[priority] || "Basse"}</Badge>;
};

export default function Vigilance() {
  const { hasPermission } = useAuth();
  const canWrite = hasPermission("invoices.write");
  const [alerts, setAlerts] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [sending, setSending] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settings, setSettings] = useState({
    sender_email: "", recipient_email: "", cc_email: "",
    notification_day: 10, enabled: false,
  });
  const [savingSettings, setSavingSettings] = useState(false);
  const [history, setHistory] = useState([]);
  const [activeTab, setActiveTab] = useState("alerts");

  const monthOptions = useMemo(genMonths, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/vigilance/default-month");
        setSelectedMonth(data.month);
      } catch {
        const now = new Date();
        setSelectedMonth(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`);
      } finally { setInitialLoading(false); }
    })();
    (async () => {
      try {
        const { data } = await api.get("/vigilance/settings");
        setSettings(data);
      } catch { /* silent */ }
    })();
  }, []);

  useEffect(() => { if (selectedMonth) fetchAlerts(); }, [selectedMonth]);

  const fetchAlerts = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/vigilance?month=${selectedMonth}`);
      setAlerts(data.alerts || []);
      setStats({
        total_alerts: data.total_alerts, high_priority: data.high_priority,
        total_at_risk_ht: data.total_at_risk_ht,
        recurring_invoices_detected: data.recurring_invoices_detected,
        prev_month: data.prev_month, prev_month_label: data.prev_month_label,
        two_months_ago: data.two_months_ago, two_months_ago_label: data.two_months_ago_label,
        month_label: data.month_label,
      });
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };

  const fetchHistory = async () => {
    try {
      const { data } = await api.get("/vigilance/history");
      setHistory(data.notifications || []);
    } catch { /* silent */ }
  };

  const ignore = async (a) => {
    try {
      await api.post("/vigilance/ignore", null, {
        params: { client_key: a.client_key, month: selectedMonth,
                  amount: a.expected_amount_ht },
      });
      toast.success("Alerte ignorée pour ce mois");
      fetchAlerts();
    } catch (e) { toast.error(apiError(e)); }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await api.post("/vigilance/settings", settings);
      toast.success("Paramètres enregistrés");
      setSettingsOpen(false);
    } catch (e) { toast.error(apiError(e)); }
    finally { setSavingSettings(false); }
  };

  const sendNotification = async () => {
    if (!settings.recipient_email) {
      toast.error("Configure d'abord l'adresse destinataire");
      setSettingsOpen(true); return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/vigilance/send-notification", null,
                                        { params: { month: selectedMonth } });
      if (data.alerts_sent > 0) toast.success(`Email envoyé avec ${data.alerts_sent} alerte(s)`);
      else toast.info("Aucune alerte à envoyer");
      fetchHistory();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSending(false); }
  };

  if (initialLoading) return (
    <div className="flex items-center justify-center min-h-[400px]">
      <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
    </div>
  );

  return (
    <div className="space-y-6" data-testid="vigilance-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"
              style={{ fontFamily: "Manrope, sans-serif" }}>
            <AlertTriangle className="w-6 h-6 text-amber-500" />
            Vigilance Facturation
          </h1>
          <p className="text-sm text-slate-500 mt-1">Détection intelligente des factures récurrentes manquantes</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <MissingVsLastMonthButton month={selectedMonth} />
          <Button variant="outline" size="sm" onClick={fetchAlerts} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Actualiser
          </Button>
          <Select value={selectedMonth || ""} onValueChange={setSelectedMonth}>
            <SelectTrigger className="w-48" data-testid="month-select">
              <Calendar className="w-4 h-4 mr-2 text-slate-400" />
              <SelectValue placeholder="Sélectionner un mois" />
            </SelectTrigger>
            <SelectContent>
              {monthOptions.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
            </SelectContent>
          </Select>
          {canWrite && (
            <Button variant="outline" size="sm" onClick={() => setSettingsOpen(true)}
                     data-testid="vigilance-settings-btn">
              <Settings className="w-4 h-4 mr-2" /> Paramètres
            </Button>
          )}
        </div>
      </div>

      <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-blue-100 rounded-lg"><Info className="w-5 h-5 text-blue-600" /></div>
            <div className="text-sm text-blue-900">
              <p className="font-semibold mb-1">Comment fonctionne la vigilance intelligente ?</p>
              <p className="text-blue-800">
                Analyse des factures des <strong>2 derniers mois</strong> ({stats.two_months_ago_label} et {stats.prev_month_label}).
                Si un client a été facturé le même montant sur les 2 mois et n'a <strong>pas encore été facturé ce mois-ci</strong>,
                une alerte apparaît. Elle disparaît dès que la facture est saisie.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className={stats.total_alerts > 0 ? "border-red-200 bg-red-50" : "border-green-200 bg-green-50"}>
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">Alertes</p>
              <p className={`text-2xl font-bold ${stats.total_alerts > 0 ? "text-red-600" : "text-green-600"}`}
                  data-testid="vigilance-total-alerts">{stats.total_alerts || 0}</p>
            </div>
            {stats.total_alerts > 0
              ? <AlertTriangle className="w-8 h-8 text-red-400" />
              : <CheckCircle className="w-8 h-8 text-green-400" />}
          </CardContent>
        </Card>
        <Card className="border-amber-200 bg-amber-50"><CardContent className="p-4 flex items-center justify-between">
          <div><p className="text-sm text-slate-600">Priorité haute</p>
            <p className="text-2xl font-bold text-amber-600">{stats.high_priority || 0}</p></div>
          <AlertCircle className="w-8 h-8 text-amber-400" />
        </CardContent></Card>
        <Card className="border-blue-200 bg-blue-50"><CardContent className="p-4 flex items-center justify-between">
          <div><p className="text-sm text-slate-600">CA à risque</p>
            <p className="text-2xl font-bold text-blue-600">{fmtEUR(stats.total_at_risk_ht)}</p></div>
          <Euro className="w-8 h-8 text-blue-400" />
        </CardContent></Card>
        <Card><CardContent className="p-4 flex items-center justify-between">
          <div><p className="text-sm text-slate-600">Récurrences détectées</p>
            <p className="text-2xl font-bold text-slate-900">{stats.recurring_invoices_detected || 0}</p></div>
          <TrendingUp className="w-8 h-8 text-slate-400" />
        </CardContent></Card>
      </div>

      <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); if (v === "history") fetchHistory(); }}>
        <div className="flex items-center justify-between">
          <TabsList>
            <TabsTrigger value="alerts"><AlertTriangle className="w-4 h-4 mr-2" /> Alertes ({stats.total_alerts || 0})</TabsTrigger>
            <TabsTrigger value="history"><History className="w-4 h-4 mr-2" /> Historique envois</TabsTrigger>
          </TabsList>
          {activeTab === "alerts" && stats.total_alerts > 0 && canWrite && (
            <Button onClick={sendNotification} disabled={sending}
                     className="bg-blue-600 hover:bg-blue-700 text-white"
                     data-testid="vigilance-send-notification-btn">
              {sending ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Envoyer notification
            </Button>
          )}
        </div>
        <TabsContent value="alerts" className="mt-4">
          {loading ? (
            <Card><CardContent className="p-8 text-center">
              <RefreshCw className="w-8 h-8 animate-spin mx-auto text-slate-400" />
            </CardContent></Card>
          ) : alerts.length === 0 ? (
            <Card className="border-green-200"><CardContent className="p-8 text-center">
              <CheckCircle className="w-12 h-12 mx-auto text-green-500 mb-4" />
              <h3 className="text-lg font-semibold text-slate-900">Aucune alerte</h3>
              <p className="text-slate-500 mt-1">
                Toutes les factures récurrentes ont été émises pour {stats.month_label}
              </p>
            </CardContent></Card>
          ) : (
            <Card>
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Client</TableHead>
                    <TableHead className="text-right">Montant attendu</TableHead>
                    <TableHead className="text-center">Priorité</TableHead>
                    <TableHead>Dernière facture ({stats.prev_month_label})</TableHead>
                    <TableHead>Facture {stats.two_months_ago_label}</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <AnimatePresence>
                    {alerts.map((a, i) => (
                      <motion.tr key={`${a.client_key}-${a.rounded_amount}`}
                                  initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                                  exit={{ opacity: 0, x: -100 }} transition={{ delay: i * 0.05 }}
                                  className="hover:bg-slate-50" data-testid={`vigilance-alert-row-${i}`}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                            <span className="font-medium">{a.client_name}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <span className="font-mono font-bold text-lg">{fmtEUR(a.expected_amount_ht)}</span>
                          <span className="text-slate-500 text-sm ml-1">HT</span>
                        </TableCell>
                        <TableCell className="text-center"><PriorityBadge priority={a.priority} /></TableCell>
                        <TableCell><div className="text-sm">
                          <p className="font-medium">{a.last_invoice_m1?.numero || "—"}</p>
                          <p className="text-slate-500">{fmtDate(a.last_invoice_m1?.date)} • {fmtEUR(a.last_invoice_m1?.amount_ht)}</p>
                        </div></TableCell>
                        <TableCell><div className="text-sm">
                          <p className="font-medium">{a.invoice_m2?.numero || "—"}</p>
                          <p className="text-slate-500">{fmtDate(a.invoice_m2?.date)} • {fmtEUR(a.invoice_m2?.amount_ht)}</p>
                        </div></TableCell>
                        <TableCell className="text-right">
                          {canWrite && (
                            <Button variant="ghost" size="sm" onClick={() => ignore(a)}
                                     data-testid={`vigilance-ignore-${i}`}
                                     title="Ignorer pour ce mois">
                              <EyeOff className="w-4 h-4" />
                            </Button>
                          )}
                        </TableCell>
                      </motion.tr>
                    ))}
                  </AnimatePresence>
                </TableBody>
              </Table>
            </Card>
          )}
        </TabsContent>
        <TabsContent value="history" className="mt-4">
          <Card>
            {history.length === 0 ? (
              <CardContent className="p-8 text-center">
                <History className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                <h3 className="text-lg font-semibold text-slate-900">Aucun historique</h3>
                <p className="text-slate-500 mt-1">Aucune notification n'a encore été envoyée</p>
              </CardContent>
            ) : (
              <Table>
                <TableHeader><TableRow className="bg-slate-50">
                  <TableHead>Date d'envoi</TableHead><TableHead>Mois concerné</TableHead>
                  <TableHead className="text-center">Alertes</TableHead>
                  <TableHead className="text-right">CA à risque</TableHead>
                  <TableHead>Destinataires</TableHead><TableHead>Envoyé par</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {history.map((n, i) => (
                    <TableRow key={i}>
                      <TableCell><div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-green-500" />
                        {new Date(n.sent_at).toLocaleString("fr-FR")}
                      </div></TableCell>
                      <TableCell>{n.month}</TableCell>
                      <TableCell className="text-center"><Badge variant="outline">{n.alerts_count}</Badge></TableCell>
                      <TableCell className="text-right font-mono">{fmtEUR(n.total_at_risk)}</TableCell>
                      <TableCell className="text-sm text-slate-500">{(n.recipients || []).join(", ")}</TableCell>
                      <TableCell className="text-sm text-slate-500">{n.sent_by}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="vigilance-settings-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"
                          style={{ fontFamily: "Manrope, sans-serif" }}>
              <Settings className="w-5 h-5" /> Paramètres des notifications
            </DialogTitle><DialogDescription className="sr-only">Détails de la modale.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="sender_email">Adresse d'envoi</Label>
              <Input id="sender_email" value={settings.sender_email || ""}
                      onChange={(e) => setSettings({ ...settings, sender_email: e.target.value })}
                      placeholder="notifications@ton-domaine.fr"
                      data-testid="vigilance-sender-email" />
              <p className="text-xs text-slate-500">Le domaine sera vérifié par Resend avant l'envoi.</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="recipient_email">Adresse destinataire *</Label>
              <Input id="recipient_email" type="email" value={settings.recipient_email || ""}
                      onChange={(e) => setSettings({ ...settings, recipient_email: e.target.value })}
                      placeholder="comptabilite@example.com"
                      data-testid="vigilance-recipient-email" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cc_email">Adresse en copie (CC)</Label>
              <Input id="cc_email" type="email" value={settings.cc_email || ""}
                      onChange={(e) => setSettings({ ...settings, cc_email: e.target.value })}
                      placeholder="direction@example.com" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="notification_day">Jour d'envoi automatique</Label>
              <Select value={String(settings.notification_day || 10)}
                       onValueChange={(v) => setSettings({ ...settings, notification_day: parseInt(v, 10) })}>
                <SelectTrigger id="notification_day"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[...Array(28)].map((_, i) =>
                    <SelectItem key={i + 1} value={String(i + 1)}>Le {i + 1} de chaque mois</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between pt-4 border-t">
              <div>
                <Label htmlFor="enabled" className="font-medium">Notifications automatiques</Label>
                <p className="text-xs text-slate-500">Envoi mensuel automatique</p>
              </div>
              <Switch id="enabled" checked={settings.enabled}
                       onCheckedChange={(v) => setSettings({ ...settings, enabled: v })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSettingsOpen(false)}>Annuler</Button>
            <Button onClick={saveSettings} disabled={savingSettings}
                     className="bg-blue-600 hover:bg-blue-700 text-white"
                     data-testid="vigilance-settings-save-btn">
              {savingSettings ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                                 : <CheckCircle className="w-4 h-4 mr-2" />}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
